import axios from 'axios';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any[];
  };
  metadata?: {
    requestId: string;
    timestamp: string;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

// Suppress unused interface warning - this is used for type documentation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _ApiResponseType<T> = ApiResponse<T>;

class ApiClient {
  private client: any;

  constructor() {
    const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://api.devtalks.headforthe.cloud';

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('id_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();

            // Retry original request with new token
            const token = localStorage.getItem('id_token');
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }

            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(): Promise<void> {
    const refreshTokenValue = localStorage.getItem('refresh_token');

    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    try {
      // Use Cognito's token endpoint directly (same logic as AuthContext)
      const hostedUiUrl = process.env.REACT_APP_COGNITO_HOSTED_UI_URL;
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '511hte4jso728me3kp6nlvtaqg';

      let cognitoDomain: string;
      if (hostedUiUrl && hostedUiUrl.trim() !== '') {
        cognitoDomain = hostedUiUrl.replace('https://', '');
      } else {
        // Fallback
        cognitoDomain = `ratemytalk-dev-auth.auth.eu-west-2.amazoncognito.com`;
      }

      const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`;

      const refreshParams = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshTokenValue
      });

      const refreshResponse = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: refreshParams.toString()
      });

      if (!refreshResponse.ok) {
        const errorData = await refreshResponse.text();
        console.error('Token refresh failed:', errorData);
        throw new Error(`Token refresh failed: ${refreshResponse.status} ${refreshResponse.statusText}`);
      }

      const tokenData = await refreshResponse.json();
      const { access_token, id_token } = tokenData;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('id_token', id_token);
    } catch (error) {
      // Clear tokens on refresh failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('id_token');
      throw error;
    }
  }

  private handleAuthFailure(): void {
    // Clear tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');

    // Redirect to login page
    window.location.href = '/login';
  }

  // HTTP Methods
  async get(url: string, config?: any): Promise<any> {
    return this.client.get(url, config);
  }

  async post(url: string, data?: any, config?: any): Promise<any> {
    return this.client.post(url, data, config);
  }

  async put(url: string, data?: any, config?: any): Promise<any> {
    return this.client.put(url, data, config);
  }

  async patch(url: string, data?: any, config?: any): Promise<any> {
    return this.client.patch(url, data, config);
  }

  async delete(url: string, config?: any): Promise<any> {
    return this.client.delete(url, config);
  }

  // Utility methods
  getBaseURL(): string {
    return this.client.defaults.baseURL || '';
  }

  setAuthToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
